import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Zap, Palette, ArrowRight, Grid3x3, X, Check, Search, Upload, Paintbrush, Loader2 } from 'lucide-react';
import FanDeck from '../components/home/FanDeck/FanDeck';
import { useTheme, useColor, useBrand } from '../context/ColorContext';
import { toast } from 'sonner';
import api from '../services/api';
import BeforeAfterSlider from '../components/ui/BeforeAfterSlider';

// Helper: determine if text should be light or dark based on bg color
const isLightColor = (hex) => {
    if (!hex) return true;
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};

// Color Grid Overlay - Shows all colors in a scrollable grid
const ColorGridOverlay = React.memo(({ colors, activeColor, onSelectColor, onClose, brandPrimaryColor, collectionName }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const INITIAL_VISIBLE_COUNT = 32;
    const LOAD_MORE_COUNT = 32;
    const [searchQuery, setSearchQuery] = useState('');
    const getVisibleCountForIndex = useCallback((index) => {
        if (index < 0) return INITIAL_VISIBLE_COUNT;
        const needed = Math.ceil((index + 1) / LOAD_MORE_COUNT) * LOAD_MORE_COUNT;
        return Math.max(INITIAL_VISIBLE_COUNT, needed);
    }, []);
    const [visibleCount, setVisibleCount] = useState(() => {
        const activeIndex = colors.findIndex((color) =>
            (activeColor?.id && color.id === activeColor.id) ||
            (color.code === activeColor.code && color.name === activeColor.name)
        );
        return getVisibleCountForIndex(activeIndex);
    });
    const [isClosing, setIsClosing] = useState(false);
    const gridRef = useRef(null);
    const loadMoreRef = useRef(null);
    const activeRef = useRef(null);
    const hasMountedRef = useRef(false);
    const hasRestoredPositionRef = useRef(false);

    const activeColorIndex = useMemo(
        () => colors.findIndex((color) =>
            (activeColor?.id && color.id === activeColor.id) ||
            (color.code === activeColor.code && color.name === activeColor.name)
        ),
        [colors, activeColor?.id, activeColor.code, activeColor.name]
    );

    const filteredColors = useMemo(() => {
        if (!searchQuery.trim()) return colors;
        const q = searchQuery.toLocaleLowerCase('tr-TR');
        return colors.filter(c =>
            c.name?.toLocaleLowerCase('tr-TR').includes(q) ||
            c.code?.toLocaleLowerCase('tr-TR').includes(q)
        );
    }, [colors, searchQuery]);

    const visibleColors = useMemo(
        () => filteredColors.slice(0, visibleCount),
        [filteredColors, visibleCount]
    );

    const activeFilteredIndex = useMemo(
        () => filteredColors.findIndex((color) =>
            (activeColor?.id && color.id === activeColor.id) ||
            (color.code === activeColor.code && color.name === activeColor.name)
        ),
        [filteredColors, activeColor?.id, activeColor.code, activeColor.name]
    );

    const hasMore = visibleCount < filteredColors.length;

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        const isSearching = Boolean(searchQuery.trim());
        const nextVisibleCount = isSearching
            ? INITIAL_VISIBLE_COUNT
            : getVisibleCountForIndex(activeColorIndex);

        setVisibleCount(nextVisibleCount);
        hasRestoredPositionRef.current = false;
        if (gridRef.current) {
            gridRef.current.scrollTop = 0;
        }
    }, [searchQuery, activeColorIndex, getVisibleCountForIndex]);

    useEffect(() => {
        if (searchQuery.trim()) return;
        if (activeFilteredIndex < 0) return;

        const requiredVisibleCount = getVisibleCountForIndex(activeFilteredIndex);
        if (visibleCount < requiredVisibleCount) {
            setVisibleCount(requiredVisibleCount);
        }
    }, [searchQuery, activeFilteredIndex, visibleCount, getVisibleCountForIndex]);

    useEffect(() => {
        if (searchQuery.trim()) return;
        if (hasRestoredPositionRef.current) return;
        if (activeFilteredIndex < 0) return;
        if (activeFilteredIndex >= visibleCount) return;
        if (!activeRef.current) return;

        activeRef.current.scrollIntoView({ block: 'center' });
        hasRestoredPositionRef.current = true;
    }, [searchQuery, activeFilteredIndex, visibleCount]);

    useEffect(() => {
        if (!hasMore || !loadMoreRef.current || !gridRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                setVisibleCount((prev) => Math.min(filteredColors.length, prev + LOAD_MORE_COUNT));
            },
            {
                root: gridRef.current,
                rootMargin: '200px 0px',
                threshold: 0.1
            }
        );

        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [hasMore, filteredColors.length]);

    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => onClose(), 350);
    }, [onClose]);

    const handleColorSelect = useCallback((color, idx) => {
        onSelectColor(color, idx);
        setIsClosing(true);
        setTimeout(() => onClose(), 350);
    }, [onSelectColor, onClose]);

    return (
        <div className="fixed inset-0 z-[110] flex flex-col">
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/60 backdrop-blur-xl transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'animate-fadeIn'}`}
                onClick={handleClose}
            />

            {/* Panel */}
            <div className={`relative z-10 flex flex-col h-full max-h-[100dvh] ${isClosing ? 'animate-slide-down-out' : 'animate-slide-up-in'}`}>
                {/* Spacer for top */}
                <div className="shrink-0 h-16 lg:h-20" onClick={handleClose} />

                {/* Main card */}
                <div className={`flex-1 min-h-0 flex flex-col rounded-t-[2rem] lg:rounded-t-[3rem] shadow-2xl overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                    {/* Drag handle */}
                    <div className="flex justify-center pt-3 pb-1 shrink-0">
                        <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-black/10'}`} />
                    </div>

                    {/* Header */}
                    <div className="px-5 lg:px-8 pt-3 pb-4 shrink-0">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <span className="text-[9px] lg:text-[10px] font-black uppercase tracking-[0.3em] opacity-40">
                                    Renk Paleti
                                </span>
                                <h2 className={`text-lg lg:text-2xl font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    {collectionName}
                                </h2>
                            </div>
                            <div className="flex items-center gap-3">
                                <span
                                    className="text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-wider"
                                    style={{ backgroundColor: `${brandPrimaryColor}15`, color: brandPrimaryColor }}
                                >
                                    {colors.length} Renk
                                </span>
                                <button
                                    onClick={handleClose}
                                    className={`p-2.5 rounded-xl transition-all active:scale-90 ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-slate-900'}`}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Search */}
                        {colors.length > 12 && (
                            <div className={`relative flex items-center rounded-xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                                <Search size={14} className="absolute left-3 opacity-30" />
                                <input
                                    type="text"
                                    placeholder="Renk ara..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`w-full pl-9 pr-4 py-2.5 text-xs font-medium bg-transparent outline-none placeholder:opacity-40 ${isDark ? 'text-white' : 'text-slate-900'}`}
                                />
                                {searchQuery && (
                                    <button onClick={() => setSearchQuery('')} className="absolute right-3 opacity-40 hover:opacity-100">
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Color Grid */}
                    <div ref={gridRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 lg:px-8 pb-8 scrollbar-hide">
                        <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2.5 lg:gap-3">
                            {visibleColors.map((color, idx) => {
                                const isActive = color.code === activeColor.code && color.name === activeColor.name;
                                const light = isLightColor(color.code);

                                return (
                                    <button
                                        key={color.id || idx}
                                        ref={isActive ? activeRef : null}
                                        onClick={() => handleColorSelect(color, idx)}
                                        className={`group relative flex flex-col items-center gap-1.5 transition-all duration-300 ${isActive ? 'scale-105' : 'hover:scale-105'}`}
                                    >
                                        {/* Color swatch */}
                                        <div
                                            className={`relative w-full aspect-square rounded-2xl shadow-lg transition-all duration-300 group-active:scale-90 ${isActive
                                                ? 'ring-[3px] shadow-xl'
                                                : 'ring-1 group-hover:shadow-xl group-hover:ring-2'
                                                }`}
                                            style={{
                                                backgroundColor: color.code,
                                                '--tw-ring-color': isActive ? brandPrimaryColor : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)')
                                            }}
                                        >
                                            {/* Check mark for active */}
                                            {isActive && (
                                                <div className="absolute inset-0 flex items-center justify-center animate-scale-in">
                                                    <div
                                                        className="w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center shadow-lg"
                                                        style={{ backgroundColor: light ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.3)' }}
                                                    >
                                                        <Check size={14} className="text-white" strokeWidth={3} />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Hover shine effect */}
                                            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
                                        </div>

                                        {/* Color name */}
                                        <span className={`text-[8px] lg:text-[9px] font-bold uppercase tracking-wide leading-tight text-center line-clamp-2 w-full px-0.5 transition-colors ${isActive
                                            ? (isDark ? 'text-white' : 'text-slate-900')
                                            : (isDark ? 'text-white/50' : 'text-slate-400')
                                            }`}>
                                            {color.name}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {hasMore && (
                            <div ref={loadMoreRef} className="py-5 text-center">
                                <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white/35' : 'text-slate-400'}`}>
                                    Daha fazla renk yukleniyor...
                                </span>
                            </div>
                        )}

                        {filteredColors.length === 0 && searchQuery && (
                            <div className="py-20 text-center">
                                <Search size={32} className="mx-auto mb-3 opacity-10" />
                                <p className={`text-xs font-bold uppercase tracking-widest ${isDark ? 'text-white/30' : 'text-slate-300'}`}>
                                    Sonuç bulunamadı
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Bottom safe area with active color preview */}
                    <div className={`shrink-0 px-5 lg:px-8 py-4 border-t flex items-center gap-4 ${isDark ? 'border-white/5 bg-slate-900/80' : 'border-black/5 bg-white/80'} backdrop-blur-md`}>
                        <div
                            className="w-10 h-10 rounded-xl shadow-lg border-2 shrink-0 transition-colors duration-500"
                            style={{ backgroundColor: activeColor.code, borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.9)' }}
                        />
                        <div className="min-w-0 flex-1">
                            <p className={`text-xs font-black uppercase tracking-tight truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {activeColor.name}
                            </p>
                            <p className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                                {activeColor.code}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="px-5 py-2.5 rounded-xl text-white text-[10px] font-black uppercase tracking-widest active:scale-95 transition-transform shadow-lg"
                            style={{ backgroundColor: brandPrimaryColor }}
                        >
                            Tamam
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

ColorGridOverlay.displayName = 'ColorGridOverlay';

// Standardized color info card to reduce code duplication
const ColorBadge = React.memo(({ color, label }) => (
    <div className="p-4 lg:p-6 rounded-3xl bg-slate-50 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center gap-4 transition-all hover:bg-slate-100 dark:hover:bg-white/10">
        <div
            className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl shadow-xl border-4 border-white dark:border-slate-800 shrink-0 transition-colors duration-500"
            style={{ backgroundColor: color.code }}
        />
        <div className="space-y-0.5">
            <span className="text-[10px] font-black text-slate-400 dark:text-white/40 uppercase tracking-widest leading-none">{label}</span>
            <p className="text-base lg:text-lg font-black dark:text-white uppercase leading-tight truncate max-w-[120px] lg:max-w-none">{color.name}</p>
            <p className="text-[9px] font-bold text-slate-400 dark:text-white/40 tracking-tighter uppercase">{color.code}</p>
        </div>
    </div>
));

ColorBadge.displayName = 'ColorBadge';

const KartelaDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { brandData, activeColor, setActiveColorIndex, isFanDeckOpen, setIsFanDeckOpen, isDbLoading } = useColor();
    const { activeBrand } = useBrand();
    const [startY, setStartY] = useState(0);
    const [selectedImage, setSelectedImage] = useState(null);
    const [lightboxImage, setLightboxImage] = useState(null);
    const [isColorGridOpen, setIsColorGridOpen] = useState(false);

    // Inline user image upload (Hemen Dene)
    const [inlineUserImage, setInlineUserImage] = useState(null);
    const inlineFileInputRef = useRef(null);

    // AI States for inline visualizer
    const [inlineProcessedImage, setInlineProcessedImage] = useState(null);
    const [isInlineProcessingAI, setIsInlineProcessingAI] = useState(false);

    useEffect(() => {
        setInlineProcessedImage(null);
        setIsInlineProcessingAI(false);
        setInlineUserImage(null);
    }, [selectedImage]);

    const handleInlineFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setInlineUserImage(ev.target.result);
            setInlineProcessedImage(null);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    }, []);

    const processInlineImageWithAI = async () => {
        const imgUrl = inlineUserImage || selectedImage?.url || selectedImage;
        if (!imgUrl) return;
        setIsInlineProcessingAI(true);
        toast.info('Yapay zeka odayı boyuyor. Lütfen bekleyin...', { duration: 5000 });

        try {
            const defaultPrompt = `Paint the walls of this room with color hex ${activeColor.code}. Keep the furniture, lighting, and textures realistic and intact. Only change the wall paint color.`;
            const promptTemplate = activeCollection?.aiPrompt || defaultPrompt;
            const prompt = promptTemplate.replace(/\{color\}/g, activeColor.code);

            const fixedUrl = imgUrl.startsWith('http') || imgUrl.startsWith('data:') ? imgUrl : window.location.origin + imgUrl;

            const res = await api.post('/fal-ai/edit', {
                prompt: prompt,
                image_url: fixedUrl
            });
            
            if (res.data?.image?.url) {
                setInlineProcessedImage(res.data.image.url);
                toast.success('Oda başarıyla boyandı!');
            } else if (res.data?.image_url) {
                setInlineProcessedImage(res.data.image_url);
                toast.success('Oda başarıyla boyandı!');
            } else if (res.data?.images?.[0]?.url) {
                setInlineProcessedImage(res.data.images[0].url);
                toast.success('Oda başarıyla boyandı!');
            } else {
                throw new Error("Geçersiz API yanıtı.");
            }
        } catch (error) {
            console.error("AI İşleme Hatası:", error);
            const errMsg = error.response?.data?.error || "Oda boyanırken bir hata oluştu.";
            toast.error(errMsg, { duration: 6000 });
        } finally {
            setIsInlineProcessingAI(false);
        }
    };

    const handleTouchStart = useCallback((e) => setStartY(e.touches[0].clientY), []);
    const handleTouchEnd = useCallback((e) => {
        const endY = e.changedTouches[0].clientY;
        const diff = startY - endY;
        if (diff > 50) setIsFanDeckOpen(true);
        else if (diff < -50) setIsFanDeckOpen(false);
    }, [startY, setIsFanDeckOpen]);

    const handleGridColorSelect = useCallback((color) => {
        const globalIndex = brandData.COLORS.findIndex(c => (c.id && c.id === color.id) || c.code === color.code);
        if (globalIndex !== -1) {
            setActiveColorIndex(globalIndex);
        }
    }, [brandData.COLORS, setActiveColorIndex]);

    const activeCollection = useMemo(() =>
        brandData.COLLECTIONS.find(c => c.slug === slug || String(c.id) === String(slug)),
        [brandData.COLLECTIONS, slug]);

    const aiEnabled = activeCollection?.aiColoringEnabled !== false;

    const openImage = useCallback((img) => {
        if (aiEnabled) setSelectedImage(img);
        else setLightboxImage(img);
    }, [aiEnabled]);

    // SEO & GEO: Dynamic Metadata & Structured Data
    React.useEffect(() => {
        if (activeCollection) {
            const title = `${activeCollection.name} | ${activeBrand.name} Renk Koleksiyonu`;
            const desc = activeCollection.metaDesc || activeCollection.subtitle || activeCollection.description || `${activeCollection.name} özel renk kartelası ile mekanlarınızı canlandırın.`;

            document.title = title;

            // Meta description
            let metaDesc = document.querySelector('meta[name="description"]');
            if (!metaDesc) {
                metaDesc = document.createElement('meta');
                metaDesc.name = 'description';
                document.head.appendChild(metaDesc);
            }
            metaDesc.setAttribute('content', desc);

            // JSON-LD for AI Engines
            const jsonLd = {
                "@context": "https://schema.org/",
                "@type": "ProductCollection",
                "name": activeCollection.name,
                "description": desc,
                "brand": {
                    "@type": "Brand",
                    "name": activeBrand.name
                },
                "image": activeCollection.coverImage || activeCollection.image
            };

            let script = document.getElementById('json-ld-schema');
            if (!script) {
                script = document.createElement('script');
                script.id = 'json-ld-schema';
                script.type = 'application/ld+json';
                document.head.appendChild(script);
            }
            script.text = JSON.stringify(jsonLd);
        }
    }, [activeCollection, activeBrand.name]);

    const primaryRgb = useMemo(() => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(activeBrand.primaryColor);
        return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '90, 166, 104';
    }, [activeBrand.primaryColor]);

    // Buttons attached to the active FanDeck card: "Tüm Renkler" + "Odanı Canlandır"
    const activeCardExtra = useMemo(() => (
        <div className="flex flex-col items-center gap-2">
            {activeCollection?.colors?.length > 0 && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsColorGridOpen(true);
                    }}
                    className="px-4 py-2 rounded-full font-black uppercase tracking-[0.12em] shadow-lg flex items-center justify-center gap-1.5 text-[9px] backdrop-blur-md border whitespace-nowrap active:scale-95 transition-transform bg-white/90 text-slate-800 border-white/40 hover:bg-white"
                >
                    <Grid3x3 size={12} />
                    <span>Tüm Renkler</span>
                    <span className="text-[8px] font-black px-1 py-0.5 rounded bg-black/10">{activeCollection.colors.length}</span>
                </button>
            )}
            {aiEnabled && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        const firstImage = activeCollection?.images?.[0];
                        if (firstImage) setSelectedImage(firstImage);
                        else toast.info('Bu koleksiyon için henüz görsel eklenmemiş.');
                    }}
                    className="px-5 py-3 rounded-full font-black uppercase tracking-[0.15em] text-white shadow-2xl animate-pulsing-glow flex items-center justify-center gap-2 text-[10px] backdrop-blur-md border border-white/20 whitespace-nowrap active:scale-95 transition-transform"
                    style={{ backgroundColor: activeBrand.primaryColor }}
                >
                    <Zap size={15} fill="currentColor" />
                    <span>Odanı Canlandır</span>
                </button>
            )}
        </div>
    ), [activeBrand.primaryColor, activeCollection?.colors, activeCollection?.images, aiEnabled]);

    // Loading State: Prevent demo flash
    // Moved after hooks to comply with Rules of Hooks
    if (isDbLoading && brandData.COLLECTIONS.length === 0) {
        return (
            <div className="fixed inset-0 bg-white dark:bg-slate-950 flex flex-col items-center justify-center z-50">
                <div className="relative">
                    <div className="w-20 h-20 border-4 border-slate-100 dark:border-white/5 rounded-full animate-spin border-t-primary-500"
                        style={{ borderTopColor: activeBrand.primaryColor }} />
                    <Palette className="absolute inset-0 m-auto text-slate-200 dark:text-white/10 animate-pulse" size={32} />
                </div>
                <p className="mt-6 text-sm font-bold tracking-widest text-slate-400 dark:text-white/20 uppercase animate-pulse">
                    Koleksiyon Yükleniyor...
                </p>
            </div>
        );
    }

    if (!activeCollection) {
        return (
            <div className={`pt-48 text-center ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                <Palette className="mx-auto mb-6 opacity-20" size={64} />
                <h2 className="text-2xl font-black uppercase tracking-tighter mb-4">Koleksiyon bulunamadı</h2>
                <button onClick={() => navigate('/kartelalar')} className="px-8 py-3 bg-slate-900 text-white rounded-full font-black uppercase tracking-widest text-xs">Geri Dön</button>
            </div>
        );
    }

    const galleryItems = activeCollection.images || [];

    return (
        <div className={`relative min-h-screen pt-20 pb-48 flex flex-col overflow-x-hidden ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>

            {/* EXPANDED VIEW (Hides main Fandeck for performance) */}
            {selectedImage && (
                <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col lg:flex-row animate-slide-down-in h-[100dvh] overflow-hidden">
                    {/* Visualizer Area - Mobile: dvh-based; Desktop: flex */}
                    <div className="relative shrink-0 h-[40dvh] lg:h-auto lg:flex-[2] lg:shrink overflow-hidden select-none group transition-colors duration-700" style={{ backgroundColor: activeColor.code }}>
                        {/* Modern Desktop Backdrop (Blurred) */}
                        <div className="absolute inset-0 hidden lg:block opacity-30 pointer-events-none">
                            <img
                                src={selectedImage.url || selectedImage}
                                alt=""
                                className="w-full h-full object-cover blur-[80px] scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black/40" />
                        </div>

                        <div className="absolute inset-0 opacity-10 bg-[url('/noise.svg')] mix-blend-overlay" />

                        <div className="relative h-full w-full z-10 flex items-center justify-center lg:justify-end">
                            {isInlineProcessingAI ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center z-[25] gap-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
                                    <Loader2 size={48} className="lg:w-16 lg:h-16 animate-spin text-white" style={{ color: activeBrand.primaryColor }} />
                                    <div className="flex flex-col items-center text-center px-4">
                                        <h4 className="text-white font-black uppercase tracking-widest text-xs lg:text-sm mb-1">DÜFA AI ÇALIŞIYOR</h4>
                                        <p className="text-white/70 text-[10px] font-bold tracking-wider">{activeColor.name} rengi odanıza uygulanıyor...</p>
                                    </div>
                                </div>
                            ) : inlineProcessedImage ? (
                                <div className="absolute inset-0 w-full h-full z-[5]">
                                    <BeforeAfterSlider beforeImage={inlineUserImage || selectedImage?.url || selectedImage} afterImage={inlineProcessedImage} />
                                </div>
                            ) : (
                                <img
                                    src={inlineUserImage || selectedImage.url || selectedImage}
                                    alt="Visualizer"
                                    className="w-full h-full object-cover shadow-[0_0_100px_rgba(0,0,0,0.5)] animate-in zoom-in-95 duration-700 pointer-events-none"
                                />
                            )}
                        </div>

                        <button
                            onClick={() => setSelectedImage(null)}
                            className="absolute top-3 left-3 lg:top-6 lg:left-6 z-20 p-2.5 lg:p-4 bg-white/10 hover:bg-white/20 rounded-xl lg:rounded-2xl text-white backdrop-blur-xl border border-white/10 transition-all flex items-center gap-2 group/back"
                        >
                            <ChevronLeft size={18} className="lg:w-6 lg:h-6 group-hover/back:-translate-x-1 transition-transform" />
                            <span className="font-black text-[10px] lg:text-xs tracking-widest uppercase hidden md:inline">Koleksiyona Dön</span>
                        </button>

                        {/* Mobile: floating color name badge at bottom of image */}
                        <div className="lg:hidden absolute bottom-2 right-3 z-20 px-2.5 py-1 rounded-full bg-black/30 backdrop-blur-md border border-white/10">
                            <span className="text-white text-[9px] font-black uppercase tracking-[0.2em] drop-shadow">{activeColor.name}</span>
                        </div>
                    </div>

                    {/* Control Panel - Mobile: compact stacked; Desktop: scrollable + fixed FanDeck */}
                    <div className="relative flex-1 min-h-0 bg-white dark:bg-slate-900 flex flex-col shadow-[0_-10px_30px_rgba(0,0,0,0.1)] lg:shadow-[-30px_0_60px_rgba(0,0,0,0.3)] overflow-hidden border-t border-black/5 dark:border-white/5 lg:border-t-0 lg:border-l z-20">

                        {/* DESKTOP: scrollable header + always-visible AI bar (no clipping) */}
                        <div className="hidden lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
                            {/* Scrollable info area */}
                            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 pt-8 pb-4 space-y-4 scrollbar-hide">
                                <div>
                                    <span className="text-[10px] font-black tracking-[0.3em] text-[#5AA668] uppercase">Seçili Uygulama</span>
                                    <h2 className="text-2xl xl:text-3xl font-black uppercase tracking-tight dark:text-white truncate">{activeCollection.name}</h2>
                                </div>

                                <ColorBadge color={activeColor} label="Seçili Renk" />
                            </div>

                            {/* Sticky AI bar - always visible above FanDeck */}
                            <div className="shrink-0 px-6 pt-3 pb-4 border-t border-black/5 dark:border-white/5 bg-white dark:bg-slate-900">
                                <div className="flex items-center gap-2.5 mb-3">
                                    <div className="w-9 h-9 rounded-xl bg-[#5AA668] flex items-center justify-center text-white shadow-md shrink-0">
                                        <Zap size={18} fill="currentColor" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h4 className="text-[12px] font-black uppercase tracking-tight dark:text-white leading-tight">Kendi Odanı Canlandır</h4>
                                        <p className="text-[10px] font-medium text-slate-500 dark:text-white/50 leading-tight">Fotoğraflayın, saniyelerde DÜFA renkleriyle test edin.</p>
                                    </div>
                                </div>
                                {inlineUserImage ? (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={processInlineImageWithAI}
                                            disabled={isInlineProcessingAI}
                                            className={`flex-1 py-3 rounded-xl font-black uppercase tracking-[0.15em] text-[11px] shadow-lg flex items-center justify-center gap-2 transition-all ${isInlineProcessingAI ? 'bg-[#5AA668]/50 text-white/60 cursor-not-allowed' : 'bg-[#5AA668] text-white hover:brightness-110 active:scale-95'}`}
                                        >
                                            {isInlineProcessingAI ? <Loader2 size={14} className="animate-spin" /> : <Paintbrush size={14} />}
                                            {isInlineProcessingAI ? 'İŞLENİYOR...' : 'DUVARLARI RENKLENDİR'}
                                        </button>
                                        <button
                                            onClick={() => { setInlineUserImage(null); setInlineProcessedImage(null); }}
                                            disabled={isInlineProcessingAI}
                                            className="shrink-0 px-3 py-3 rounded-xl bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-white/70 hover:bg-slate-200 active:scale-95 transition-all"
                                            aria-label="Görseli sıfırla"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => inlineFileInputRef.current?.click()}
                                        className="w-full py-3 rounded-xl bg-[#5AA668] text-white text-[11px] font-black uppercase tracking-[0.15em] shadow-lg hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
                                    >
                                        <Upload size={14} /> HEMEN DENE <ArrowRight size={14} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* MOBILE: compact info bar (color + collection + AI button) */}
                        <div className="lg:hidden shrink-0 px-3 py-2.5 flex flex-col gap-2 border-b border-black/5 dark:border-white/5 relative z-30 bg-white dark:bg-slate-900">
                            <div className="flex items-center gap-2.5 min-w-0">
                                <div
                                    className="w-9 h-9 rounded-xl shadow-md border-2 border-white dark:border-slate-700 shrink-0 transition-colors duration-500"
                                    style={{ backgroundColor: activeColor.code }}
                                />
                                <div className="min-w-0 flex-1">
                                    <span className="text-[8px] font-black tracking-[0.22em] text-[#5AA668] uppercase block leading-tight truncate">{activeCollection.name}</span>
                                    <div className="flex items-baseline gap-2 min-w-0">
                                        <span className="text-[11px] font-black uppercase tracking-tight truncate dark:text-white">{activeColor.name}</span>
                                        <span className="text-[9px] font-bold uppercase tracking-wider opacity-40 dark:text-white/50 shrink-0">{activeColor.code}</span>
                                    </div>
                                </div>
                            </div>
                            {inlineUserImage ? (
                                <div className="flex gap-2">
                                    <button
                                        onClick={processInlineImageWithAI}
                                        disabled={isInlineProcessingAI}
                                        className={`flex-1 py-2.5 rounded-lg font-black uppercase tracking-[0.12em] text-[10px] shadow-md flex items-center justify-center gap-1.5 transition-all ${isInlineProcessingAI ? 'bg-[#5AA668]/50 text-white/60 cursor-not-allowed' : 'bg-[#5AA668] text-white active:scale-95'}`}
                                    >
                                        {isInlineProcessingAI ? <Loader2 size={14} className="animate-spin" /> : <Paintbrush size={14} />}
                                        {isInlineProcessingAI ? 'İŞLENİYOR...' : 'DUVARLARI BOYA'}
                                    </button>
                                    <button
                                        onClick={() => { setInlineUserImage(null); setInlineProcessedImage(null); }}
                                        disabled={isInlineProcessingAI}
                                        className="shrink-0 px-3 py-2.5 rounded-lg bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-white/70 active:scale-95 transition-all"
                                        aria-label="Görseli sıfırla"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => inlineFileInputRef.current?.click()}
                                    className="w-full py-2.5 rounded-lg bg-[#5AA668] text-white text-[10px] font-black uppercase tracking-[0.15em] shadow-md flex items-center justify-center gap-2 active:scale-95 transition-all"
                                >
                                    <Upload size={14} /> HEMEN DENE <ArrowRight size={12} />
                                </button>
                            )}
                        </div>

                        {/* Shared file input */}
                        <input
                            ref={inlineFileInputRef}
                            type="file"
                            className="hidden"
                            accept="image/png,image/*"
                            onChange={handleInlineFileChange}
                        />

                        {/* FANDECK - Mobile: flex-1; Desktop: capped at 45vh so AI bar always fits */}
                        <div className="flex-1 min-h-0 lg:flex-none lg:h-[min(360px,45vh)] bg-slate-50/50 dark:bg-black/20 border-t border-slate-100 dark:border-white/5 px-3 pt-2 pb-0 lg:p-6 flex flex-col relative overflow-hidden isolate">
                            <div className="mb-1 lg:mb-4 flex items-center justify-between shrink-0 relative z-10">
                                <span className="text-[9px] lg:text-[10px] font-black text-slate-400 uppercase tracking-widest">Koleksiyon Renkleri</span>
                                <button
                                    onClick={() => setIsColorGridOpen(true)}
                                    className="flex items-center gap-1.5 text-[8px] lg:text-[9px] font-black px-2.5 py-1.5 rounded-lg uppercase transition-all hover:brightness-110 active:scale-95"
                                    style={{ backgroundColor: `${activeBrand.primaryColor}15`, color: activeBrand.primaryColor }}
                                >
                                    <Grid3x3 size={12} />
                                    Tüm Renkler
                                </button>
                            </div>
                            <div className="flex-1 w-full relative min-h-0">
                                <FanDeck
                                    isMobile={true}
                                    enableWheel={!isColorGridOpen}
                                    lift={true}
                                    activeCardExtra={null}
                                    overrideColors={activeCollection.colors}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Back Navigation */}
            <button
                onClick={() => navigate('/kartelalar')}
                className={`fixed top-28 left-4 z-50 flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md transition-all shadow-sm ${theme === 'dark' ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-black/5 text-slate-900 hover:bg-black/10'}`}
            >
                <ChevronLeft size={20} />
                <span className="text-[10px] font-black uppercase tracking-widest hidden md:inline">Koleksiyonlara Dön</span>
            </button>

            {/* Title Section */}
            <div className="container mx-auto px-4 text-center z-20 mt-4 lg:mt-10">
                <h1 className="text-4xl md:text-7xl font-black uppercase mb-4 tracking-tighter drop-shadow-sm">
                    {activeCollection.name?.toLocaleUpperCase('tr-TR')}
                </h1>
                <p className="max-w-xl mx-auto mb-10 text-base md:text-lg font-medium opacity-60 text-balance leading-relaxed">
                    {activeCollection.subtitle || activeCollection.description || 'Mekanlarınıza karakter katan özel renk seçkisi.'}
                </p>

                {galleryItems.length > 0 && aiEnabled && (
                    <button
                        onClick={() => setSelectedImage(galleryItems[0])}
                        className="px-12 py-5 mb-16 text-white rounded-full font-black uppercase tracking-[0.2em] shadow-2xl transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-3 mx-auto text-sm animate-pulsing-glow relative overflow-hidden group/main"
                        style={{ backgroundColor: activeBrand.primaryColor, boxShadow: `0 15px 30px -10px ${activeBrand.primaryColor}80` }}
                    >
                        <Zap size={20} fill="currentColor" />
                        GÖRÜNÜMLERİ KEŞFET
                    </button>
                )}
            </div>

            {/* Main Gallery Grid */}
            <div className="container mx-auto px-4 max-w-7xl relative z-20">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
                    {galleryItems.map((img, idx) => (
                        <div
                            key={idx}
                            onClick={() => openImage(img)}
                            className="aspect-video relative rounded-[1.5rem] lg:rounded-[2.5rem] overflow-hidden shadow-2xl shadow-black/5 transition-all duration-700 hover:scale-[1.02] cursor-pointer group bg-slate-100 dark:bg-white/5 border border-black/5 dark:border-white/5"
                        >
                            <div className="absolute inset-0 transition-colors duration-700" style={{ backgroundColor: activeColor.code }} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10" />
                            <img
                                src={img.url || img}
                                alt={activeCollection.name}
                                className="absolute inset-0 w-full h-full object-cover z-0 transition-transform duration-1000 group-hover:scale-110"
                                loading="lazy"
                            />
                            <div className="absolute inset-x-0 bottom-0 p-6 z-20 flex flex-col opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                                <span className="text-[10px] font-black text-white/50 uppercase tracking-widest leading-none mb-1">{activeCollection.name}</span>
                                <span className="text-base font-black text-white uppercase tracking-tight">Görünüm {idx + 1}</span>
                            </div>
                        </div>
                    ))}

                    {galleryItems.length === 0 && (
                        <div className="col-span-full py-32 text-center bg-slate-50/50 dark:bg-white/5 rounded-[4rem] border-2 border-dashed border-slate-200 dark:border-white/10 opacity-50">
                            <Palette className="mx-auto mb-6 text-slate-300 dark:text-white/20" size={64} />
                            <p className="text-slate-400 font-black uppercase tracking-widest text-xs">Henüz görsel eklenmemiş</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Standard Global FanDeck Drawer (Only visible when main view is active) */}
            {!selectedImage && (
                <div
                    className={`fixed left-0 right-0 h-[440px] transition-all duration-700 cubic-bezier(0.23, 1, 0.32, 1) flex flex-col z-50 lg:z-10 pointer-events-none
                        ${isFanDeckOpen ? 'bottom-0 translate-y-0' : 'bottom-0 translate-y-[340px]'}
                        ${window.innerWidth >= 1024 ? 'lg:translate-y-0' : ''}
                    `}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="container mx-auto px-4 h-full pointer-events-auto">
                        <div className="flex justify-center h-full w-full">
                            <div className="h-full w-full max-w-lg">
                                <FanDeck
                                    isMobile={window.innerWidth < 1024}
                                    enableWheel={!isColorGridOpen}
                                    activeCardExtra={activeCardExtra}
                                    lift={isFanDeckOpen}
                                    overrideColors={activeCollection.colors}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Lightbox (when AI coloring is disabled) */}
            {lightboxImage && (
                <div
                    className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center animate-slide-down-in"
                    onClick={() => setLightboxImage(null)}
                >
                    <button
                        onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }}
                        className="absolute top-4 right-4 lg:top-6 lg:right-6 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-2xl text-white backdrop-blur-xl border border-white/10 transition-all"
                        aria-label="Kapat"
                    >
                        <X size={22} />
                    </button>

                    {galleryItems.length > 1 && (
                        <>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const idx = galleryItems.findIndex(g => (g.url || g) === (lightboxImage.url || lightboxImage));
                                    const prev = galleryItems[(idx - 1 + galleryItems.length) % galleryItems.length];
                                    setLightboxImage(prev);
                                }}
                                className="absolute left-3 lg:left-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-2xl text-white backdrop-blur-xl border border-white/10 transition-all"
                                aria-label="Önceki"
                            >
                                <ChevronLeft size={22} />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const idx = galleryItems.findIndex(g => (g.url || g) === (lightboxImage.url || lightboxImage));
                                    const next = galleryItems[(idx + 1) % galleryItems.length];
                                    setLightboxImage(next);
                                }}
                                className="absolute right-3 lg:right-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-2xl text-white backdrop-blur-xl border border-white/10 transition-all"
                                aria-label="Sonraki"
                            >
                                <ChevronLeft size={22} className="rotate-180" />
                            </button>
                        </>
                    )}

                    <img
                        src={lightboxImage.url || lightboxImage}
                        alt={activeCollection.name}
                        onClick={(e) => e.stopPropagation()}
                        className="max-w-[95vw] max-h-[90vh] object-contain rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300"
                    />
                </div>
            )}

            {/* Color Grid Overlay */}
            {isColorGridOpen && activeCollection.colors?.length > 0 && (
                <ColorGridOverlay
                    colors={activeCollection.colors}
                    activeColor={activeColor}
                    onSelectColor={handleGridColorSelect}
                    onClose={() => setIsColorGridOpen(false)}
                    brandPrimaryColor={activeBrand.primaryColor}
                    collectionName={activeCollection.name}
                />
            )}

            {/* Texture Overlay */}
            <div className="fixed inset-0 pointer-events-none bg-[url('/noise.svg')] opacity-[0.03] mix-blend-overlay z-[5]" />
        </div>
    );
};

export default React.memo(KartelaDetail);


